"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

import { isNotUndefinedOrNull } from "@daytrip/utils";

import { useDropdownArrowKeysNavigation } from "../dropdown/Dropdown.hooks";
import { DropdownOption } from "../dropdown/DropdownOption";
import { useGoogleApi } from "../GoogleMapsApiProvider";
import type { IInputProps } from "../input/Input";
import { useClickOutsideHandler } from "../useClickOutsideHandler";

import { usePlacesService, useValidateAddress } from "./AddressAutocomplete.hooks";
import { DropdownBody, DropdownWrapper, PoweredByGoogle, StyledInput } from "./AddressAutocomplete.styles";
import type { AddressLocation, RestrictToAreaOptions } from "./AddressAutocomplete.types";
import { isPlaceWithinRadius, parsePlaceResultResponse } from "./AddressAutocomplete.utils";

const BASE_IMAGE_URL = "https://daytrip2.imgix.net";

export interface IGoogleAddressAutocompleteProps extends Omit<IInputProps, "value"> {
    value?: string;
    onAddressChange?: (value: AddressLocation) => void;
    restrictToArea?: RestrictToAreaOptions;
    restrictToCountries?: string[];
    restrictToTypes?: string[];
    onPredictionsChanged?: (predictions: any[]) => void;
}

type LocationWithOnlyName = Pick<AddressLocation, "name">;

const AddressAutocompleteInputInner: React.FC<React.PropsWithChildren<IGoogleAddressAutocompleteProps>> = (props) => {
    const { isLoaded } = useGoogleApi();
    const {
        onAddressChange,
        restrictToArea,
        restrictToCountries,
        restrictToTypes,
        onPredictionsChanged,
        ...inputProps
    } = props;
    const { onBlur, isValid, spacing } = props;

    const { getPlacePredictions, placesService, placePredictions, isPlacePredictionsLoading } = usePlacesService({
        restrictedArea: restrictToArea,
        restrictedCountries: restrictToCountries,
        placePredictionsTypes: restrictToTypes,
    });
    const { validateAddress, isValid: isAddressValid } = useValidateAddress({
        defaultAddress: props.value,
    });

    const wrapperRef = useRef<HTMLDivElement>(null);
    const googleAutocompleteRef = useRef<HTMLInputElement>(null);
    const currentLocation = useRef<LocationWithOnlyName | null>(
        props.value
            ? {
                  name: props.value,
              }
            : null,
    );
    const [isOpen, setIsOpened] = useState(false);

    useClickOutsideHandler(wrapperRef, isOpen, setIsOpened);

    const callOnChange = (location: AddressLocation) => {
        if (googleAutocompleteRef.current) {
            googleAutocompleteRef.current.value = location.name;
        }
        if (onAddressChange) {
            onAddressChange(location);
        }
        setIsOpened(false);
        googleAutocompleteRef.current?.blur();
    };

    const setAddress = (description: string, placeId: string) => {
        placesService?.getDetails(
            {
                placeId,
                fields: ["formatted_address", "geometry", "address_components"],
            },
            (placeDetails, status) => {
                if (!placeDetails || status !== "OK") {
                    return;
                }
                currentLocation.current = parsePlaceResultResponse(description, placeDetails);
                callOnChange(currentLocation.current as AddressLocation);
            },
        );
    };

    const placePredictionsWithinRadius = useMemo(
        () => placePredictions.filter((prediction) => isPlaceWithinRadius(prediction, restrictToArea)),
        [placePredictions],
    );
    const { focused, setFocused, handleArrowNavigation } = useDropdownArrowKeysNavigation(
        false,
        true,
        placePredictionsWithinRadius.map((place) => ({
            label: place.description,
            value: place.place_id,
        })),
        (option) => {
            setAddress(option.label, option.value.toString());
        },
        setIsOpened,
    );

    const onInputChange = (inputValue: string) => {
        setFocused(undefined);
        setIsOpened(!!googleAutocompleteRef?.current?.value);

        getPlacePredictions(inputValue);

        if (onAddressChange) {
            onAddressChange({ name: inputValue, countryIsoCode: "", position: undefined });
        }
    };

    const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        if (!googleAutocompleteRef.current) {
            throw Error("Ref is not set for google autocomplete input.");
        }
        googleAutocompleteRef.current.setAttribute("autocomplete", `autocomplete-${Math.random()}`);

        if (placePredictionsWithinRadius.length) {
            setIsOpened(true);
        }
        if (inputProps.onFocus) {
            inputProps.onFocus(event);
        }
    };

    const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
        const hasFocusedOut = wrapperRef.current && !wrapperRef.current.matches(":focus-within");
        validateAddress(currentLocation?.current?.name || "", { area: restrictToArea, countries: restrictToCountries });

        if (hasFocusedOut) {
            setIsOpened(false);
            if (onBlur) {
                onBlur(event);
            }
        }
    };

    useEffect(() => {
        // If the input has an exact match from results, we enforce it
        // This accounts for manually entered/modified addresses (copy/paste, delete/undos, etc)
        const inputValue = googleAutocompleteRef.current?.value;
        const resultMatch = placePredictionsWithinRadius.find((prediction) => prediction.description === inputValue);
        if (resultMatch) {
            setAddress(resultMatch.description, resultMatch.place_id);
        }

        if (onPredictionsChanged) {
            onPredictionsChanged(placePredictions);
        }
    }, [placePredictions]);

    const isOpenAndHasResults = isOpen && !isPlacePredictionsLoading && placePredictionsWithinRadius.length > 0;

    function isInputValid() {
        if (isOpen) {
            return true;
        }

        // If the validity is explicitly set, use that
        if (isNotUndefinedOrNull(isValid) && isValid !== null) {
            return isValid;
        }

        if (googleAutocompleteRef.current?.value) {
            return isAddressValid;
        }

        return true;
    }

    if (!isLoaded) {
        return null;
    }

    return (
        <DropdownWrapper ref={wrapperRef} onKeyDown={handleArrowNavigation} spacing={spacing}>
            <StyledInput
                {...(inputProps as IInputProps)}
                isAutocompleteDisabled
                inputRef={googleAutocompleteRef}
                onChange={onInputChange}
                onFocus={onFocus}
                onBlur={handleBlur}
                isOpen={isOpenAndHasResults}
                isPlacePredictionsLoading={isPlacePredictionsLoading}
                isValid={isInputValid()}
                ariaInvalid={!isInputValid()}
            />
            {isOpenAndHasResults && (
                <DropdownBody isValid={isInputValid()} tabIndex={isOpen ? -1 : undefined}>
                    {placePredictionsWithinRadius.map(({ description, place_id }, index) => (
                        <DropdownOption
                            data-cy="addressOption"
                            key={place_id}
                            content={{ label: description, value: place_id }}
                            isFocused={focused === index}
                            onSelect={() => {
                                setAddress(description, place_id);
                                setFocused(index);
                            }}
                        />
                    ))}
                    <PoweredByGoogle>
                        <img
                            src={`${BASE_IMAGE_URL}/site/powered_by_google_on_white_hdpi.png`}
                            alt="Powered by Google"
                            width={144}
                            height={18}
                        />
                    </PoweredByGoogle>
                </DropdownBody>
            )}
        </DropdownWrapper>
    );
};

export const AddressAutocompleteInput: React.FC<React.PropsWithChildren<IGoogleAddressAutocompleteProps>> = (props) => {
    const { isLoaded } = useGoogleApi();

    /**
     * The AddressAutocompleteInputInner component uses the Google Maps api through `usePlacesService`.
     * The `usePlacesService` hook will try to load the Places library as soon as it enters the component tree.
     * This can cause a race condition where the Places library is not loaded yet when the `usePlacesService` hook is called,
     * thereby causing an error when `usePlacesService` tries to new up a `google.maps.places.AutocompleteService` instance.
     *
     * Note: this happens when the `AddressAutocompleteInput` is rendered on the first pass when rendering the page,
     * not when it is rendered on a subsequent pass (e.g. when the user clicks on a button to show the input).
     * */
    if (!isLoaded) {
        // We return a skeleton instead of `null` to avoid the UI jumping around
        return (
            <DropdownWrapper spacing={props.spacing}>
                <StyledInput isOpen={false} />
            </DropdownWrapper>
        );
    }

    return <AddressAutocompleteInputInner {...props} />;
};
